import React from "react";

import { toast } from "react-toastify";
import { isEnabled } from "utils/featureFlags";

import { push } from "connected-react-router";

import moment from "moment";

import * as DatasetAPI from "api/DatasetAPI";

import * as CommunityMarketAPI from "api/CommunityMarketAPI";

import * as accountsReducer from "containers/Accounts/reducer";
import * as loginReducer from "containers/Login/reducer";
import * as foldersReducer from "containers/Folders/reducer";

const UPDATE_FIELD = "communityMarket/UPDATE_FIELD";
const RESET = "communityMarket/RESET";

const initialMarket = {
  tabView: "all",
  isLoadingItems: true,
  isCartOpen: false,
  showPublishModal: false,
  cartItems: undefined,
  sortBy: {
    val: "created_date",
    dir: "DSC"
  },
  priceFilter: {
    free: false,
    min: undefined,
    max: undefined
  }
};

export const FILTER_OPTIONS = {
  TOOL: {
    category: "tool",
    options: [
      {
        value: "FINE_TUNED_MODEL",
        label: "Fine-Tuned Model"
      },
      {
        value: "USE_CASE",
        label: "Use Case"
      },
      {
        value: "CHAT_APP",
        label: "Chat App"
      }
    ]
  },
  BASE_MODEL: {
    category: "base model",
    options: [
      {
        value: "text-bison@002",
        label: "PaLM 2"
      },
      {
        value: "mistral",
        label: "Mistral"
      }
    ]
  },
  USE_CASE: {
    category: "use cases",
    options: [
      {
        value: "Data Analytics",
        label: "Data Analytics"
      },
      {
        value: "Document Summarization",
        label: "Document Summarization"
      },
      {
        value: "NLQ",
        label: "NLQ"
      },
      {
        value: "10K Comparison",
        label: "10K Comparison"
      },
      {
        value: "NLQ for Finance",
        label: "NLQ for Finance"
      },
      {
        value: "CSV",
        label: "CSV"
      }
    ]
  },
  LICENSE: {
    category: "license",
    options: [
      {
        value: "Open Source",
        label: "Open Source"
      },
      {
        value: "Proprietary",
        label: "Proprietary"
      }
    ]
  }
  // PRICE: {
  //   category: "price",
  //   free: undefined,
  //   min: undefined,
  //   max: undefined,
  // }
};

export const USE_CASE_OPTIONS = [
  {
    value: "Data Analytics",
    label: "Data Analytics"
  },
  {
    value: "Document Summarization",
    label: "Document Summarization"
  },
  {
    value: "NLQ",
    label: "NLQ"
  },
  {
    value: "10K Comparison",
    label: "10K Comparison"
  },
  {
    value: "NLQ for Finance",
    label: "NLQ for Finance"
  },
  {
    value: "CSV",
    label: "CSV"
  }
];

export const SORTING_OPTIONS = [
  {
    value: {
      val: "created_date",
      dir: "DSC"
    },
    label: "Newest"
  },
  {
    value: {
      val: "created_date",
      dir: "ASC"
    },
    label: "Oldest"
  },
  {
    value: {
      val: "price",
      dir: "ASC"
    },
    label: "Price (Lowest)"
  },
  {
    value: {
      val: "price",
      dir: "DSC"
    },
    label: "Price (Highest)"
  }
];

export function getFilterOptions(state) {
  const tabView = getTabView(state);
  switch (tabView) {
    case "all":
      return [FILTER_OPTIONS.TOOL, FILTER_OPTIONS.BASE_MODEL, FILTER_OPTIONS.USE_CASE];
    case "models":
      return [FILTER_OPTIONS.BASE_MODEL, FILTER_OPTIONS.USE_CASE];
    default:
      return [];
  }
}

function marketReducer(state = initialMarket, action) {
  switch (action.type) {
    case UPDATE_FIELD:
      return {
        ...state,
        [action.key]: action.data
      };
    case RESET:
      return initialMarket;
    default:
      return state;
  }
}

export function resetMarket() {
  return (dispatch, getState) => {
    dispatch({ type: RESET });
  };
}

/* Selectors */
export function getBase(state) {
  return state.communityMarket;
}

export function getIsCommunityMarket() {
  return isEnabled("community-market");
}

export function getIsFiltersOpen(state) {
  return getBase(state)?.isFiltersOpen;
}

export function getIsCartOpen(state) {
  return getBase(state)?.isCartOpen;
}

export function getIsLoadingItems(state) {
  return getBase(state)?.isLoadingItems;
}

export function getShowPublishModal(state) {
  return getBase(state)?.showPublishModal;
}

export function getPublishItem(state) {
  return getBase(state)?.publishItem ?? {};
}

export function getTypeFilters(state) {
  return getBase(state)?.typeFilters ?? [];
}

export function getMetadataFilters(state) {
  return getBase(state)?.metadataFilters ?? [];
}

export function getPriceFilter(state) {
  return getBase(state)?.priceFilter ?? {};
}

export function getActiveFilters(state) {
  return getBase(state)?.activeFilters ?? [];
}

export function getModels(state) {
  return getBase(state)?.models ?? [];
}

export function getChatApps(state) {
  return getBase(state)?.chatApps ?? [];
}

export function getUseCases(state) {
  return getBase(state)?.useCases ?? [];
}

export function getAllListings(state) {
  const models = getModels(state);
  const chatApps = getChatApps(state);
  const useCases = getUseCases(state);

  return [...models, ...chatApps, ...useCases] ?? [];
}

export function getFilteredListings(state) {
  const typeFilters = getTypeFilters(state);
  const priceFilter = getPriceFilter(state);
  const metadataFilters = getMetadataFilters(state);
  const allListings = getAllListings(state);

  const hasFilters =
    priceFilter?.free === true ||
    priceFilter?.min ||
    priceFilter?.max ||
    typeFilters?.length > 0 ||
    metadataFilters?.length > 0;

  if (!hasFilters) {
    return allListings;
  }

  const isValidType = (item) => typeFilters?.length < 1 || typeFilters.includes(item?.type);

  //  Filter logic for price. If there are no other price filters and free is pressed, only shows free.
  //  Otherwise it'll show all cards that would normally be in the price range + Free.
  //  validMin also evalutes if priceFilter.min is undefined but max exists and price is less than max.

  const isValidPrice = (item) => {
    const validMax = !priceFilter.max || item.price <= priceFilter.max;
    const validMin =
      (priceFilter.free !== true && !priceFilter.min) ||
      (priceFilter.free === true && (item.price <= 0 || (!priceFilter.min && item.price <= priceFilter.max))) ||
      (priceFilter.min && item.price >= priceFilter.min);

    return validMin && validMax;
  };

  const getOptionValues = (options) => options.map((o) => o?.value);

  const hasOptionInFilters = (key) => {
    const optionValues = getOptionValues(FILTER_OPTIONS[key]?.options || []);
    return optionValues.some((option) => metadataFilters.includes(option));
  };

  const hasBaseModelOptions = hasOptionInFilters("BASE_MODEL");
  const hasUseCaseOptions = hasOptionInFilters("USE_CASE");

  const isValidBaseModel = (item) =>
    metadataFilters?.length < 1 || !hasBaseModelOptions || metadataFilters.includes(item?.metadata?.base_model);

  const isValidUseCase = (item) =>
    metadataFilters?.length < 1 || !hasUseCaseOptions || metadataFilters.includes(item?.metadata?.use_case);

  let filteredListings = [...allListings];

  const retListings = filteredListings.filter((item) => {
    const validBaseModel = isValidBaseModel(item);
    const validUseCase = isValidUseCase(item);
    const validMetadata = validBaseModel && validUseCase;

    return isValidType(item) && isValidPrice(item) && validMetadata;
  });

  return retListings;
}

export function getSortedData(state) {
  return getBase(state)?.sortedData ?? [];
}

export function getTabView(state) {
  return getBase(state)?.tabView;
}

export function getCartItemsArray(state) {
  return getBase(state)?.cartItemsArray ?? [];
}

export function getSortBy(state) {
  return getBase(state)?.sortBy;
}

export function fetchAllMarketListings() {
  return (dispatch, getState) => {
    dispatch(updateIsLoadingItems(true));
    return Promise.all([dispatch(fetchModels(true)), dispatch(fetchChatApps(true)), dispatch(fetchUseCases(true))])
      .then(() => {
        dispatch(updateIsLoadingItems(false));
      })
      .catch((error) => {
        console.log(error);
      });
  };
}

export function fetchModels(skipLoading = false) {
  return (dispatch, getState) => {
    if (!skipLoading) {
      dispatch(updateIsLoadingItems(true));
    }
    return CommunityMarketAPI.getModels()
      .then((response) => {
        const nResponse = parseModels(response);
        dispatch(updateModels(nResponse));
        if (!skipLoading) {
          dispatch(updateIsLoadingItems(false));
        }
        return response;
      })
      .catch((error) => {
        console.log(error);
        dispatch(fetchModels());
      });
  };
}

export function parseModels(models) {
  return models.map((model) => {
    return parseModel(model);
  });
}

export function parseModel(model) {
  const retModel = {
    name: model?.name,
    id: model?.id,
    price: model?.price,
    created_date: model?.created_date,
    created_by: model?.created_by,
    metadata: {
      base_model: model?.metadata?.base_model,
      use_case: model?.metadata?.use_case,
      summary: model?.metadata?.summary,
      details: model?.metadata?.details,
      downloads: model?.metadata?.downloads,
      like_count: model?.metadata?.like_count,
      dislike_count: model?.metadata?.dislike_count
    },
    source: model?.source,
    type: model?.type
  };
  return retModel;
}

export function publishModel(model) {
  return (dispatch, getState) => {
    dispatch(updateIsLoadingItems(true));
    return CommunityMarketAPI.publishModel(model)
      .then((response) => {
        const nResponse = parseModels(response);
        dispatch(updateModels(nResponse));
        dispatch(updateIsLoadingItems(false));
        toast("Your item is now listed on the Marketplace", {
          type: toast.TYPE.SUCCESS,
          position: toast.POSITION.TOP_CENTER,
          autoClose: 7500
        });
        return response;
      })
      .catch((error) => {
        console.log(error);
        toast("There was a problem adding your item to the Marketplace", {
          type: toast.TYPE.ERROR,
          position: toast.POSITION.TOP_CENTER,
          autoClose: 7500
        });
      });
  };
}

//Temporary function for creating unique ID for store listing
//
//

export function generateId() {
  const currentTime = Date.now();
  const randomValue = Math.random().toString(36).substring(2, 7);
  const retId = `${currentTime}-${randomValue}`;
  return retId;
}

export function fetchChatApps(skipLoading = false) {
  return (dispatch, getState) => {
    if (!skipLoading) {
      dispatch(updateIsLoadingItems(true));
    }
    return CommunityMarketAPI.getChatApps()
      .then((response) => {
        dispatch(updateChatApps(response));
        if (!skipLoading) {
          dispatch(updateIsLoadingItems(false));
        }
        return response;
      })
      .catch((error) => {
        console.log(error);
        dispatch(fetchChatApps());
      });
  };
}

export function fetchUseCases(skipLoading = false) {
  return (dispatch, getState) => {
    if (!skipLoading) {
      dispatch(updateIsLoadingItems(true));
    }
    return CommunityMarketAPI.getUseCases()
      .then((response) => {
        dispatch(updateUseCases(response));
        if (!skipLoading) {
          dispatch(updateIsLoadingItems(false));
        }
        return response;
      })
      .catch((error) => {
        console.log(error);
        dispatch(fetchUseCases());
      });
  };
}

export function updateModels(modelsObj) {
  return { type: UPDATE_FIELD, key: "models", data: modelsObj };
}

export function updateChatApps(chatAppsObj) {
  return { type: UPDATE_FIELD, key: "chatApps", data: chatAppsObj };
}

export function updateUseCases(useCasesObj) {
  return { type: UPDATE_FIELD, key: "useCases", data: useCasesObj };
}

export function updateTabView(tabView) {
  return { type: UPDATE_FIELD, key: "tabView", data: tabView };
}

export function updateIsFiltersOpen(bool) {
  return { type: UPDATE_FIELD, key: "isFiltersOpen", data: bool };
}

export function updateIsCartOpen(bool) {
  return { type: UPDATE_FIELD, key: "isCartOpen", data: bool };
}

export function updateIsLoadingItems(bool) {
  return (dispatch, getState) => {
    dispatch({ type: UPDATE_FIELD, key: "isLoadingItems", data: bool });

    if (bool === false) {
      return dispatch(sortData());
    }

    return;
  };
}

export function updateShowPublishModal(bool) {
  return { type: UPDATE_FIELD, key: "showPublishModal", data: bool };
}

export function updatePublishItem(obj) {
  return { type: UPDATE_FIELD, key: "publishItem", data: obj };
}

export function updateCartItemsArray(cartArray) {
  return { type: UPDATE_FIELD, key: "cartItemsArray", data: cartArray };
}

export function updateSortBy(sortBy) {
  return (dispatch, getState) => {
    dispatch({ type: UPDATE_FIELD, key: "sortBy", data: sortBy });
    dispatch(sortData());
  };
}

export function updateTypeFilters(array) {
  return (dispatch, getState) => {
    dispatch({ type: UPDATE_FIELD, key: "typeFilters", data: array });
    dispatch(sortData());
  };
}

export function updateMetadataFilters(array) {
  return (dispatch, getState) => {
    dispatch({ type: UPDATE_FIELD, key: "metadataFilters", data: array });
    dispatch(sortData());
  };
}

export function updatePriceFilter(priceObj) {
  return (dispatch, getState) => {
    dispatch({ type: UPDATE_FIELD, key: "priceFilter", data: priceObj });
    dispatch(sortData());
  };
}

export function updateSortedData(array) {
  return { type: UPDATE_FIELD, key: "sortedData", data: array };
}

export function resetAllFilters() {
  return (dispatch, getState) => {
    dispatch(updateTypeFilters([]));
    dispatch(updateMetadataFilters([]));
    dispatch(
      updatePriceFilter({
        free: false,
        min: undefined,
        max: undefined
      })
    );
  };
}

export function addToCart(itemId) {
  return (dispatch, getState) => {
    const currentCart = getCartItemsArray(getState());
    const updatedCart = [itemId, ...currentCart];

    return dispatch(updateCartItemsArray(updatedCart));
  };
}

export function removeFromCart(itemId) {
  return (dispatch, getState) => {
    const currentCart = getCartItemsArray(getState());
    const updatedCart = currentCart.filter((id) => id !== itemId);

    return dispatch(updateCartItemsArray(updatedCart));
  };
}

export function addItemToFolder(item, folderId) {
  return (dispatch, getState) => {
    return dispatch(CommunityMarketAPI.addItemToFolder(item, folderId))
      .then((response) => {
        console.log(response);
        return response;
      })
      .catch((error) => {
        console.log(error);
        return Promise.resolve();
      });
  };
}

export function addAllItemsToFolder(cartItems, folderId) {
  return (dispatch, getState) => {
    const promises = cartItems.map((item) => dispatch(addItemToFolder(item, folderId)));

    return Promise.all(promises)
      .then((responses) => {
        toast("Items have been added to your folder", {
          type: toast.TYPE.SUCCESS,
          position: toast.POSITION.TOP_CENTER,
          autoClose: 7500
        });
        return responses;
      })
      .catch((error) => {
        console.log(error);
        toast("There was a problem adding the items to your folder", {
          type: toast.TYPE.ERROR,
          position: toast.POSITION.TOP_CENTER,
          autoClose: 7500
        });
      });
  };
}

function compareStrings(a, b) {
  return a.localeCompare(b);
}

function compareNumbers(a, b) {
  return a < b ? -1 : a > b ? 1 : 0;
}

function compareDates(a, b) {
  const dateA = new Date(a);
  const dateB = new Date(b);
  return dateA < dateB ? -1 : 1;
}

export function sortData() {
  return (dispatch, getState) => {
    const filteredListings = getFilteredListings(getState());
    const sortObj = getSortBy(getState());

    const sortedArray = [...filteredListings].sort((a, b) => {
      const aValue = a[sortObj?.val] ?? a.metadata[sortObj?.val];
      const bValue = b[sortObj?.val] ?? b.metadata[sortObj?.val];

      if (aValue === undefined) return sortObj.dir === "ASC" ? 1 : -1;
      if (bValue === undefined) return sortObj.dir === "ASC" ? -1 : 1;

      if (sortObj?.val === "created_date") {
        return sortObj?.dir === "ASC" ? compareDates(aValue, bValue) : compareDates(bValue, aValue);
      }

      if (sortObj?.val === "price") {
        return sortObj?.dir === "ASC" ? compareNumbers(aValue, bValue) : compareNumbers(bValue, aValue);
      }

      return sortObj?.dir === "ASC" ? compareStrings(aValue, bValue) : compareStrings(bValue, aValue);
    });

    return dispatch(updateSortedData(sortedArray));
  };
}

export default marketReducer;
